import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../../components/Screen/Screen';
import { ScreenHeader } from '../../components/ScreenHeader/ScreenHeader';
import { ProgressIndicator } from '../../components/ProgressIndicator/ProgressIndicator';
import { Button } from '../../components/Button/Button';
import { copy, labels } from '../../constants';
import { useOnboardingStore } from '../../store/onboarding';
import type { OccupationCategory, FirstResponderRole } from '../../types';

export const Occupation: React.FC = () => {
  const navigate = useNavigate();
  const { updateData, setStep } = useOnboardingStore();
  const [showFirstResponderDetail, setShowFirstResponderDetail] = useState(false);

  const occupationOptions = [
    { value: 'first-responder', label: labels.occupationCategory['first-responder'] },
    { value: 'healthcare', label: labels.occupationCategory.healthcare },
    { value: 'military-veteran', label: labels.occupationCategory['military-veteran'] },
    { value: 'student', label: labels.occupationCategory.student },
    { value: 'office-professional', label: labels.occupationCategory['office-professional'] },
    { value: 'trades-labour', label: labels.occupationCategory['trades-labour'] },
    { value: 'not-working', label: labels.occupationCategory['not-working'] },
    { value: 'something-else', label: labels.occupationCategory['something-else'] }
  ];

  const firstResponderOptions = [
    { value: 'police', label: labels.firstResponderRole.police },
    { value: 'fire', label: labels.firstResponderRole.fire },
    { value: 'paramedic', label: labels.firstResponderRole.paramedic },
    { value: 'dispatch', label: labels.firstResponderRole.dispatch },
    { value: 'corrections', label: labels.firstResponderRole.corrections },
    { value: 'search-rescue', label: labels.firstResponderRole['search-rescue'] },
    { value: 'other', label: labels.firstResponderRole.other }
  ];

  const [selectedOccupation, setSelectedOccupation] = useState<OccupationCategory | null>(null);

  const handleOccupationSelect = (value: OccupationCategory) => {
    setSelectedOccupation(value);
  };

  const handleOccupationContinue = () => {
    if (!selectedOccupation) return;
    updateData({ occupation_category: selectedOccupation });

    if (selectedOccupation === 'first-responder') {
      setShowFirstResponderDetail(true);
    } else {
      updateData({ occupation_detail: null });
      setStep(7);
      navigate('/location');
    }
  };

  const handleFirstResponderSelect = (value: FirstResponderRole) => {
    updateData({ occupation_detail: value });
    setStep(7);
    navigate('/location');
  };

  const handleBack = () => {
    setShowFirstResponderDetail(false);
    updateData({ occupation_detail: null });
  };

  if (showFirstResponderDetail) {
    return (
      <Screen>
        <ProgressIndicator currentStep={6} totalSteps={8} />
        
        <ScreenHeader
          title={copy.firstResponderDetail.title}
          subtitle={copy.firstResponderDetail.subtitle}
        />
        
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
            gap: 'var(--spacing-sm)'
          }}>
          {firstResponderOptions.map((option) => (
            <Button
              key={option.value}
              variant="outline"
              size="large"
              fullWidth
              onClick={() => handleFirstResponderSelect(option.value as FirstResponderRole)}
            >
              {option.label}
            </Button>
          ))}
        </div>
        
        <div style={{ marginTop: 'var(--spacing-md)' }}>
          <Button
            variant="ghost"
            size="medium"
            fullWidth
            onClick={handleBack}
          >
            ← Back
          </Button>
        </div>
      </Screen>
    );
  }

  return (
    <Screen>
      <ProgressIndicator currentStep={6} totalSteps={8} />
      
      <ScreenHeader
        title={copy.occupation.title}
        subtitle={copy.occupation.subtitle}
      />
      
      <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
          gap: 'var(--spacing-sm)'
        }}>
        {occupationOptions.map((option) => (
          <Button
            key={option.value}
            variant="outline"
            size="large"
            fullWidth
            selected={selectedOccupation === option.value}
            onClick={() => handleOccupationSelect(option.value as OccupationCategory)}
          >
            {option.label}
          </Button>
        ))}
      </div>
      <div style={{ marginTop: 'var(--spacing-lg)' }}>
        <Button
          variant="primary"
          size="large"
          fullWidth
          disabled={!selectedOccupation}
          onClick={handleOccupationContinue}
        >
          Continue
        </Button>
      </div>
    </Screen>
  );
};
